import React from "react";
import { Formik, Field, Form } from "formik";
import MultiSelect from "form-controls/MultiSelect";
import Switch from "form-controls/Switch";
import Button from "Button";
import {
  sendingSiteDescription,
  receivingSiteDescription
} from "booking-helper";
import { bookingStatuses } from "bookingStatuses";
import "./BookingFilterForm.css";

const uniqueValues = arr =>
  arr.filter(
    ({ value }, idx) =>
      arr.findIndex(({ value: innerValue }) => innerValue === value) === idx
  );

const alphaCompare = (a, b) => {
  if (a.label === b.label) {
    return 0;
  }

  return a.label > b.label ? 1 : -1;
};

const directionOptions = [
  { value: "inbound", label: "Inbound" },
  { value: "outbound", label: "Outbound" },
  { value: "internal", label: "Internal" }
];

const formatStatusLabel = status =>
  status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const statusOptions = [
  ...Object.values(bookingStatuses).map(s => ({
    value: s,
    label: formatStatusLabel(s)
  })),
  { value: "proposed", label: "Proposed" }
];

export const haulierOptions = bookings =>
  uniqueValues(
    bookings
      .filter(b => b.haulier)
      .map(b => ({
        value: b.haulier.id,
        label: b.haulier.name
      }))
  ).sort(alphaCompare);

export const sendingSiteOptions = bookings =>
  uniqueValues(
    bookings
      .filter(
        b =>
          b.sendingSite ||
          (b.appointment &&
            b.appointment.data &&
            b.appointment.data.sending_site_description) ||
          b.origin
      )
      .map(b => {
        const label = sendingSiteDescription(b);
        const value = b.sendingSite ? b.sendingSite.id : label;
        return { value, label };
      })
      .filter(o => o.label)
  ).sort(alphaCompare);

export const receivingSiteOptions = bookings =>
  uniqueValues(
    bookings
      .filter(
        b =>
          b.receivingSite ||
          (b.appointment &&
            b.appointment.data &&
            b.appointment.data.receiving_site_description) ||
          b.origin
      )
      .map(b => {
        const label = receivingSiteDescription(b);
        const value = b.receivingSite ? b.receivingSite.id : label;
        return { value, label };
      })
      .filter(o => o.label)
  ).sort(alphaCompare);

export const loadTypeOptions = bookings =>
  uniqueValues(
    bookings
      .filter(b => b.loadType)
      .map(b => ({
        value: b.loadType,
        label: b.loadType
      }))
  ).sort(alphaCompare);

export const trailerTypeOptions = bookings =>
  uniqueValues(
    bookings
      .filter(b => b.trailerType)
      .map(b => ({
        value: b.trailerType.id,
        label: b.trailerType.name
      }))
  ).sort(alphaCompare);

export const tagOptions = bookings =>
  uniqueValues(
    bookings
      .filter(
        b =>
          b.appointment &&
          b.appointment.data &&
          b.appointment.data["journey_tag"]
      )
      .map(b => ({
        value: b.appointment.data["journey_tag"],
        label: b.appointment.data["journey_tag"]
      }))
  ).sort(alphaCompare);

export default function BookingFilterForm({
  appliedFilters,
  bookings,
  onSubmit,
  onReset
}) {
  const initialValues = {
    includeInternal: false,
    direction: [],
    status: [],
    haulier: [],
    sendingSite: [],
    receivingSite: [],
    loadType: [],
    trailerType: [],
    tag: [],
    ...appliedFilters
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      enableReinitialize
    >
      {({ setFieldValue }) => (
        <Form className="booking-filter-form">
          <Field
            name="includeInternal"
            label="Include internal bookings"
            component={Switch}
          />
          <Field
            name="direction"
            label="Direction"
            placeholder="Any"
            options={directionOptions}
            component={MultiSelect}
            onBlur={selectedItems => {
              setFieldValue("direction", selectedItems);
            }}
          />
          <Field
            name="status"
            label="Status"
            placeholder="Any"
            options={statusOptions}
            component={MultiSelect}
            onBlur={selectedItems => {
              setFieldValue("status", selectedItems);
            }}
          />
          <Field
            name="haulier"
            label="Haulier"
            placeholder="Any"
            options={haulierOptions(bookings)}
            component={MultiSelect}
            onBlur={selectedItems => {
              setFieldValue("haulier", selectedItems);
            }}
          />
          <Field
            name="sendingSite"
            label="Sending Site"
            placeholder="Any"
            options={sendingSiteOptions(bookings)}
            component={MultiSelect}
            onBlur={selectedItems => {
              setFieldValue("sendingSite", selectedItems);
            }}
          />
          <Field
            name="receivingSite"
            label="Receiving Site"
            placeholder="Any"
            options={receivingSiteOptions(bookings)}
            component={MultiSelect}
            onBlur={selectedItems => {
              setFieldValue("receivingSite", selectedItems);
            }}
          />
          <Field
            name="loadType"
            label="Load Type"
            placeholder="Any"
            options={loadTypeOptions(bookings)}
            component={MultiSelect}
            onBlur={selectedItems => {
              setFieldValue("loadType", selectedItems);
            }}
          />
          <Field
            name="trailerType"
            label="Trailer Type"
            placeholder="Any"
            options={trailerTypeOptions(bookings)}
            component={MultiSelect}
            onBlur={selectedItems => {
              setFieldValue("trailerType", selectedItems);
            }}
          />
          <Field
            name="tag"
            label="Tag"
            placeholder="Any"
            options={tagOptions(bookings)}
            component={MultiSelect}
            onBlur={selectedItems => {
              setFieldValue("tag", selectedItems);
            }}
          />
          <div className="horizontal-container">
            <Button
              type="submit"
              variant="primary"
              size="small"
              title="Apply filters"
            />
            <Button
              type="reset"
              variant="secondary"
              size="small"
              title="Reset"
              onClick={onReset}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
}
